import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  CalculatorIcon,
  
  ScaleIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  Scatter,
  ScatterChart
} from 'recharts';

interface FilterProps {
  dateRange: { start: string; end: string; preset: string };
  departments: string[];
  metrics: string[];
  detailLevel: string;
  displayType: string;
  currency: string;
}

interface ProfitabilityAnalysisProps {
  filters: FilterProps;
}

const ProfitabilityAnalysis: React.FC<ProfitabilityAnalysisProps> = ({ filters }) => {
  const { t, direction } = useLanguage();
  const [selectedView, setSelectedView] = useState('overview');

  // Profitability metrics
  const profitabilityMetrics = [
    {
      title: 'إجمالي هامش الربح',
      value: '32.5%',
      change: '+2.3%',
      trend: 'up',
      icon: ChartBarIcon,
      color: 'from-green-500 to-green-600',
      target: '35%',
      status: 'good'
    },
    {
      title: 'هامش الربح التشغيلي',
      value: '18.7%',
      change: '+1.8%',
      trend: 'up',
      icon: CalculatorIcon,
      color: 'from-blue-500 to-blue-600',
      target: '20%',
      status: 'good'
    },
    {
      title: 'هامش الربح الصافي',
      value: '12.4%',
      change: '+0.9%',
      trend: 'up',
      icon: CurrencyDollarIcon,
      color: 'from-purple-500 to-purple-600',
      target: '15%',
      status: 'warning'
    },
    {
      title: 'عائد الأصول (ROA)',
      value: '8.2%',
      change: '+1.2%',
      trend: 'up',
      icon: ArrowTrendingUpIcon,
      color: 'from-orange-500 to-orange-600',
      target: '10%',
      status: 'warning'
    },
    {
      title: 'عائد حقوق الملكية (ROE)',
      value: '15.6%',
      change: '+2.1%',
      trend: 'up',
      icon: ScaleIcon,
      color: 'from-indigo-500 to-indigo-600',
      target: '18%',
      status: 'good'
    }
  ];

  // Monthly profitability trend
  const profitabilityTrend = [
    { month: 'يناير', gross: 28.5, operating: 16.2, net: 10.8, revenue: 2100000, cost: 1501500 },
    { month: 'فبراير', gross: 29.8, operating: 17.1, net: 11.2, revenue: 2300000, cost: 1614600 },
    { month: 'مارس', gross: 31.2, operating: 17.8, net: 11.8, revenue: 2500000, cost: 1720000 },
    { month: 'أبريل', gross: 30.1, operating: 17.2, net: 11.4, revenue: 2400000, cost: 1675760 },
    { month: 'مايو', gross: 31.8, operating: 18.3, net: 12.1, revenue: 2600000, cost: 1773200 },
    { month: 'يونيو', gross: 32.5, operating: 18.7, net: 12.4, revenue: 2847500, cost: 1922062 }
  ];

  // Product/Service profitability
  const productProfitability = [
    {
      name: 'البرمجيات المالية',
      revenue: 1138000,
      cost: 682800,
      profit: 455200,
      margin: 40.0,
      units: 450,
      profitPerUnit: 1012,
      trend: '+15%'
    },
    {
      name: 'الاستشارات المالية',
      revenue: 854250,
      cost: 469837,
      profit: 384413,
      margin: 45.0,
      units: 180,
      profitPerUnit: 2136,
      trend: '+22%'
    },
    {
      name: 'التدريب والتطوير',
      revenue: 426125,
      cost: 298287,
      profit: 127838,
      margin: 30.0,
      units: 320,
      profitPerUnit: 399,
      trend: '+8%'
    },
    {
      name: 'الدعم التقني',
      revenue: 284750,
      cost: 227800,
      profit: 56950,
      margin: 20.0,
      units: 890,
      profitPerUnit: 64,
      trend: '+5%'
    },
    {
      name: 'التكامل والتنفيذ',
      revenue: 144375,
      cost: 86625,
      profit: 57750,
      margin: 40.0,
      units: 25,
      profitPerUnit: 2310,
      trend: '+35%'
    }
  ];

  // Customer segment profitability
  const segmentProfitability = [
    {
      segment: 'الشركات الكبيرة',
      revenue: 1423750,
      cost: 854250,
      profit: 569500,
      margin: 40.0,
      customers: 45,
      avgProfitPerCustomer: 12656,
      acquisitionCost: 2500,
      ltv: 45000
    },
    {
      segment: 'الشركات المتوسطة',
      revenue: 853125,
      cost: 554062,
      profit: 299063,
      margin: 35.1,
      customers: 180,
      avgProfitPerCustomer: 1662,
      acquisitionCost: 800,
      ltv: 12000
    },
    {
      segment: 'الشركات الصغيرة',
      revenue: 426563,
      cost: 298594,
      profit: 127969,
      margin: 30.0,
      customers: 520,
      avgProfitPerCustomer: 246,
      acquisitionCost: 300,
      ltv: 3500
    },
    {
      segment: 'الأفراد',
      revenue: 143962,
      cost: 115170,
      profit: 28792,
      margin: 20.0,
      customers: 502,
      avgProfitPerCustomer: 57,
      acquisitionCost: 150,
      ltv: 800
    }
  ];

  // Profitability drivers analysis
  const profitabilityDrivers = [
    {
      driver: 'تحسين الأسعار',
      impact: 'عالي',
      potentialIncrease: '3.2%',
      effort: 'متوسط',
      timeframe: '3 أشهر',
      description: 'مراجعة هيكل الأسعار وتحسين استراتيجية التسعير'
    },
    {
      driver: 'خفض التكاليف التشغيلية',
      impact: 'متوسط',
      potentialIncrease: '2.1%',
      effort: 'عالي',
      timeframe: '6 أشهر',
      description: 'تحسين الكفاءة التشغيلية وأتمتة العمليات'
    },
    {
      driver: 'تحسين مزيج المنتجات',
      impact: 'عالي',
      potentialIncrease: '2.8%',
      effort: 'متوسط',
      timeframe: '4 أشهر',
      description: 'التركيز على المنتجات عالية الربحية'
    },
    {
      driver: 'تحسين الإنتاجية',
      impact: 'متوسط',
      potentialIncrease: '1.9%',
      effort: 'متوسط',
      timeframe: '5 أشهر',
      description: 'تدريب الموظفين وتحسين الأدوات'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'excellent':
        return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-300';
      case 'good':
        return 'text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300';
      case 'warning':
        return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'critical':
        return 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'text-gray-600 bg-gray-100 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'عالي':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'متوسط':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'منخفض':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  return (
    <div className="space-y-6">
      {/* Profitability Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {profitabilityMetrics.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <motion.div
              key={metric.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`w-8 h-8 bg-gradient-to-r ${metric.color} rounded-lg p-1.5`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(metric.status)}`}>
                  {metric.status === 'good' ? 'جيد' : metric.status === 'warning' ? 'تحذير' : 'ممتاز'}
                </div>
              </div>
              
              <h3 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                {metric.title}
              </h3>
              
              <div className="flex items-baseline space-x-1 rtl:space-x-reverse mb-2">
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  {metric.value}
                </span>
                <span className={`text-xs font-medium ${
                  metric.trend === 'up' 
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-600 dark:text-red-400'
                }`}>
                  {metric.change}
                </span>
              </div>
              
              <div className="text-xs text-gray-500 dark:text-gray-400">
                المستهدف: {metric.target}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Profitability Trend Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Profitability Trend */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              اتجاه هوامش الربح الشهرية
            </h3>
            <select
              value={selectedView}
              onChange={(e) => setSelectedView(e.target.value)}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="overview">نظرة عامة</option>
              <option value="detailed">تفصيلي</option>
            </select>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={profitabilityTrend}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                dataKey="month" 
                className="text-xs"
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                className="text-xs"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip
                formatter={(value: any, name: any) => [`${value}%`, name]}
                labelStyle={{ color: '#374151' }}
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Line
                type="monotone"
                dataKey="gross"
                stroke="#10B981"
                strokeWidth={3}
                name="إجمالي هامش الربح"
                dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="operating"
                stroke="#3B82F6"
                strokeWidth={3}
                name="هامش الربح التشغيلي"
                dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="net"
                stroke="#8B5CF6"
                strokeWidth={3}
                name="هامش الربح الصافي"
                dot={{ fill: '#8B5CF6', strokeWidth: 2, r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Product Profitability */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            ربحية المنتجات والخدمات
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={productProfitability} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                type="number"
                className="text-xs"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `${value}%`}
              />
              <YAxis 
                type="category"
                dataKey="name"
                className="text-xs"
                tick={{ fontSize: 10 }}
                width={100}
              />
              <Tooltip
                formatter={(value: any) => [`${value}%`, 'هامش الربح']}
                labelStyle={{ color: '#374151' }}
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Bar dataKey="margin" fill="#3B82F6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Customer Segment Profitability */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          ربحية شرائح العملاء
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">الشريحة</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">الإيرادات</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">الربح</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">هامش الربح</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">متوسط الربح/عميل</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">قيمة العميل</th>
              </tr>
            </thead>
            <tbody>
              {segmentProfitability.map((segment, index) => (
                <motion.tr
                  key={segment.segment}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <td className="py-3 px-4">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{segment.segment}</span>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{segment.customers} عميل</div>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-900 dark:text-white font-medium">
                    {(segment.revenue / 1000).toLocaleString()} ألف ر.س
                  </td>
                  <td className="py-3 px-4 text-sm text-green-600 dark:text-green-400 font-medium">
                    {(segment.profit / 1000).toLocaleString()} ألف ر.س
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {segment.margin.toFixed(1)}%
                      </span>
                      <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 max-w-16">
                        <div 
                          className="bg-green-500 h-2 rounded-full transition-all duration-1000"
                          style={{ width: `${Math.min(segment.margin, 50)}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                    {segment.avgProfitPerCustomer.toLocaleString()} ر.س
                  </td>
                  <td className="py-3 px-4 text-sm text-blue-600 dark:text-blue-400 font-medium">
                    {segment.ltv.toLocaleString()} ر.س
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Profitability Improvement Opportunities */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <ArrowTrendingUpIcon className="w-5 h-5 ml-2 rtl:ml-0 rtl:mr-2" />
          فرص تحسين الربحية
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {profitabilityDrivers.map((driver, index) => (
            <motion.div
              key={driver.driver}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {driver.driver}
                </h4>
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getImpactColor(driver.impact)}`}>
                    {driver.impact}
                  </span>
                  <span className="text-lg font-bold text-green-600 dark:text-green-400">
                    +{driver.potentialIncrease}
                  </span>
                </div>
              </div>
              
              <div className="space-y-2 mb-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">الجهد المطلوب:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{driver.effort}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">الإطار الزمني:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{driver.timeframe}</span>
                </div>
              </div>
              
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {driver.description}
              </p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default ProfitabilityAnalysis;
